import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/StatusBadge';
import { localeLabel } from '../utils/localeFlag';

const RESOURCE_TYPES = ['PRODUCT', 'COLLECTION', 'PAGE', 'ARTICLE', 'MENU'];
const STATUSES = ['pushed', 'translated', 'failed', 'skipped'];
const PAGE_SIZE = 50;

const RESOURCE_PATH = {
  PRODUCT: 'products',
  COLLECTION: 'collections',
  PAGE: 'pages-content',
  ARTICLE: 'articles',
  MENU: 'menus',
};

function resourceLink(resourceId, resourceType) {
  const slug = RESOURCE_PATH[resourceType];
  if (!slug) return null;
  const numericId = (resourceId || '').split('/').pop();
  return numericId ? `/${slug}/${numericId}` : null;
}

function truncate(value, max = 80) {
  if (value == null) return '';
  const s = String(value);
  return s.length <= max ? s : s.slice(0, max) + '…';
}

export default function History() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const [locale, setLocale] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [locales, setLocales] = useState([]);

  useEffect(() => {
    axios.get('api/transly/locales/').then((res) => {
      setLocales(res.data.filter((l) => !l.primary));
    });
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (locale) params.locale = locale;
      if (statusFilter) params.status = statusFilter;
      if (resourceType) params.resourceType = resourceType;
      if (search) params.search = search;
      const res = await axios.get('api/transly/translation-log/', { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  }, [offset, locale, statusFilter, resourceType, search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const onFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setOffset(0);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setOffset(0);
  };

  const formatDate = (s) => (s ? new Date(s).toLocaleString() : '-');

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">History</h1>
        <span className="text-sm text-muted-foreground">{total.toLocaleString()} entries</span>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-muted-foreground mb-1">Locale</label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={locale}
              onChange={onFilterChange(setLocale)}
            >
              <option value="">All</option>
              {locales.map((l) => (
                <option key={l.locale} value={l.locale}>{localeLabel(l.locale)} — {l.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-muted-foreground mb-1">Resource type</label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={resourceType}
              onChange={onFilterChange(setResourceType)}
            >
              <option value="">All</option>
              {RESOURCE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-muted-foreground mb-1">Status</label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={statusFilter}
              onChange={onFilterChange(setStatusFilter)}
            >
              <option value="">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <form onSubmit={onSearchSubmit} className="flex flex-col flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-muted-foreground mb-1">Search source/translated</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="text contains…"
                className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
              />
              <Button type="submit" variant="outline" size="sm">Search</Button>
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSearch(''); setSearchInput(''); setOffset(0); }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">When</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Resource</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Field</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Locale</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Source</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Translation</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">SEO</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No entries match these filters.</td></tr>
              )}
              {!loading && entries.map((e) => {
                const link = resourceLink(e.resource_id, e.resource_type);
                const isExpanded = expandedId === e.id;
                const seoLabel = e.seo_approved == null ? '—' : (e.seo_approved ? 'approved' : 'corrected');
                return (
                  <React.Fragment key={e.id}>
                    <tr
                      className="border-b border-border hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : e.id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatDate(e.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="text-foreground text-xs">{e.resource_type}</div>
                        {link ? (
                          <Link
                            to={link}
                            onClick={(ev) => ev.stopPropagation()}
                            className="text-xs text-primary hover:underline font-mono"
                          >
                            {(e.resource_id || '').split('/').pop()}
                          </Link>
                        ) : (
                          <div className="text-xs font-mono text-muted-foreground">{(e.resource_id || '').split('/').pop()}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">{e.field_key}</td>
                      <td className="px-4 py-3 uppercase text-foreground">{e.target_locale}</td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[260px]">{truncate(e.source_value)}</td>
                      <td className="px-4 py-3 text-foreground max-w-[260px]">{truncate(e.translated_value)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={e.status} />
                        {e.is_outdated && (
                          <span className="ml-1 text-[10px] uppercase text-orange-600">outdated</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{seoLabel}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="px-4 py-4 bg-muted/40">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div>
                              <div className="font-medium text-muted-foreground mb-1">Source</div>
                              <pre className="whitespace-pre-wrap break-words bg-background p-2 rounded border border-border max-h-72 overflow-auto">{e.source_value || ''}</pre>
                            </div>
                            <div>
                              <div className="font-medium text-muted-foreground mb-1">Translation</div>
                              <pre className="whitespace-pre-wrap break-words bg-background p-2 rounded border border-border max-h-72 overflow-auto">{e.translated_value || ''}</pre>
                            </div>
                            {e.seo_corrections && (
                              <div className="md:col-span-2">
                                <div className="font-medium text-muted-foreground mb-1">SEO corrections</div>
                                <pre className="whitespace-pre-wrap break-words bg-background p-2 rounded border border-border max-h-72 overflow-auto">{JSON.stringify(e.seo_corrections, null, 2)}</pre>
                              </div>
                            )}
                            {e.error_message && (
                              <div className="md:col-span-2">
                                <div className="font-medium text-destructive mb-1">Error</div>
                                <pre className="whitespace-pre-wrap break-words bg-destructive/5 p-2 rounded border border-destructive/30 max-h-48 overflow-auto">{e.error_message}</pre>
                              </div>
                            )}
                            <div className="md:col-span-2 text-muted-foreground">
                              <span className="font-mono">model:</span> {e.model_used || '—'}
                              <span className="mx-3">·</span>
                              <span className="font-mono">job:</span> {e.job_id || '—'}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
